// MinhaLoja/WhatsApp/WhatsAppConfig

import React, { useState, useEffect, useRef } from 'react';
// 🔥 IMPORTAÇÕES CORRIGIDAS: Trash2 e X adicionados para evitar a tela branca
import { MessageSquare, QrCode, RefreshCw, CheckCircle2, Plus, Power, Smartphone, Phone, Trash2, X } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import io from 'socket.io-client';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const BASE_URL = 'https://app.universomidiadigital.com.br';

const WhatsAppConfig = ({ estabelecimentoId }) => {
    const [instances, setInstances] = useState([]);
    const [loadingGlobal, setLoadingGlobal] = useState(true);
    const socketRef = useRef(null);

    // Estado para o Modal Customizado (Substituindo alerts e prompts)
    const [modalConfig, setModalConfig] = useState({
        show: false,
        type: 'confirm', // 'confirm' | 'prompt'
        title: '',
        message: '',
        inputValue: '',
        isDanger: false,
        onConfirm: null,
        onCancel: null
    });

    useEffect(() => {
        // 🔥 CORREÇÃO DO WEBSOCKET: Polling garante que a conexão nunca aborte antes de estabilizar
        socketRef.current = io(BASE_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000
        });

        socketRef.current.on('connect', () => {
            socketRef.current.emit('join', estabelecimentoId);
        });

        socketRef.current.on('whatsapp-qr', ({ instanceId, qr }) => {
            setInstances(prev => prev.map(inst => 
                // 🔥 CORREÇÃO: Comparando como String para o React não ignorar o QR Code
                String(inst.id) === String(instanceId) ? { ...inst, qrCode: qr, status: 'lendo', loading: false } : inst
            ));
        });

        socketRef.current.on('whatsapp-status', (data) => {
            setInstances(prev => prev.map(inst => {
                if (String(inst.id) === String(data.instanceId)) {
                    const newStatus = data.status || 'desconectado';
                    if (newStatus === 'conectado' && inst.status !== 'conectado') {
                        toast.success(`WhatsApp conectado com sucesso!`);
                    }
                    
                    return {
                        ...inst,
                        status: newStatus,
                        device: data.device || null,
                        qrCode: newStatus === 'conectado' ? null : inst.qrCode,
                        loading: false
                    };
                }
                return inst;
            }));
        });

        carregarInstancias();

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, [estabelecimentoId]);

    const carregarInstancias = async () => {
        setLoadingGlobal(true);
        try {
            const res = await api.get(`/whatsapp/instances/${estabelecimentoId}`);
            
            if (res.data?.payload?.length > 0) {
                const formattedInstances = res.data.payload.map(inst => ({
                    ...inst,
                    status: inst.status || 'verificando',
                    qrCode: null,
                    loading: false
                }));
                setInstances(formattedInstances);
            } else {
                setInstances([]);
            }
        } catch (err) {
            console.error("Erro ao buscar instâncias:", err);
            toast.error("Falha ao carregar as conexões de WhatsApp.");
        } finally {
            setLoadingGlobal(false);
        }
    };

    // 🚀 Modal de Adicionar Instância
    const handleAddInstanceClick = () => {
        setModalConfig({
            show: true,
            type: 'prompt',
            title: 'Novo Número de WhatsApp',
            message: 'Dê um nome para este número (Ex: Suporte, Vendas, Financeiro):',
            inputValue: '',
            isDanger: false,
            onCancel: () => setModalConfig(prev => ({ ...prev, show: false })),
            onConfirm: async (nomeInstancia) => {
                if (!nomeInstancia || nomeInstancia.trim() === '') {
                    toast.error("O nome é obrigatório!");
                    return;
                }
                setModalConfig(prev => ({ ...prev, show: false }));
                setLoadingGlobal(true);
                try {
                    await api.post(`/whatsapp/instances/${estabelecimentoId}`, { name: nomeInstancia });
                    toast.success("Nova conexão criada! Gere o QR Code para parear.");
                    carregarInstancias();
                } catch (err) {
                    toast.error("Erro ao criar nova conexão.");
                } finally {
                    setLoadingGlobal(false);
                }
            }
        });
    };

    const handleGerarQR = async (instanceId) => {
        setInstances(prev => prev.map(inst => inst.id === instanceId ? { ...inst, loading: true, qrCode: null } : inst));
        try {
            await api.post(`/whatsapp/connect/${estabelecimentoId}/${instanceId}`);
            toast.success("Solicitando QR Code...");
        } catch (err) {
            toast.error("Erro ao iniciar conexão");
            setInstances(prev => prev.map(inst => inst.id === instanceId ? { ...inst, loading: false } : inst));
        }
    };

    // 🚀 Modal de Desconectar
    const handleDesconectarClick = (instanceId) => {
        setModalConfig({
            show: true,
            type: 'confirm',
            title: 'Desconectar Aparelho',
            message: 'Deseja realmente desconectar este aparelho? Você precisará escanear o QR Code novamente para voltar a usar.',
            isDanger: true,
            onCancel: () => setModalConfig(prev => ({ ...prev, show: false })),
            onConfirm: async () => {
                setModalConfig(prev => ({ ...prev, show: false }));
                setInstances(prev => prev.map(inst => inst.id === instanceId ? { ...inst, loading: true } : inst));
                try {
                    await api.post(`/whatsapp/disconnect/${estabelecimentoId}/${instanceId}`);
                    toast.success("Desconectado com sucesso!");
                } catch (err) {
                    toast.error("Erro ao desconectar");
                    setInstances(prev => prev.map(inst => inst.id === instanceId ? { ...inst, loading: false } : inst));
                }
            }
        });
    };

    // 🚀 Modal de Excluir Instância
    const handleRemoverInstanciaClick = (instanceId) => {
        setModalConfig({
            show: true,
            type: 'confirm',
            title: 'Excluir Conexão',
            message: 'ATENÇÃO: Isso vai excluir esta conexão e todos os dados de sessão associados a ela. Esta ação não pode ser desfeita. Deseja continuar?',
            isDanger: true,
            onCancel: () => setModalConfig(prev => ({ ...prev, show: false })),
            onConfirm: async () => {
                setModalConfig(prev => ({ ...prev, show: false }));
                setLoadingGlobal(true);
                try {
                    await api.delete(`/whatsapp/instances/${estabelecimentoId}/${instanceId}`);
                    toast.success("Conexão removida definitivamente!");
                    carregarInstancias();
                } catch (err) {
                    toast.error("Erro ao remover a instância.");
                    setLoadingGlobal(false);
                }
            }
        });
    };

    if (loadingGlobal && instances.length === 0) {
        return (
            <div className="ws-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <RefreshCw className="spin" size={32} color="#00a884" />
            </div>
        );
    }

    return (
        <div className="ws-container">
            {/* ================= MODAL CUSTOMIZADO ================= */}
            {modalConfig.show && (
                <div className="ws-modal-overlay" onClick={modalConfig.onCancel}>
                    <div className="ws-modal" onClick={e => e.stopPropagation()}>
                        <div className="ws-modal-header">
                            <h3>{modalConfig.title}</h3>
                            <button onClick={modalConfig.onCancel} className="ws-modal-close-btn"><X size={20} /></button>
                        </div>
                        <div className="ws-modal-body">
                            <p>{modalConfig.message}</p>
                            {modalConfig.type === 'prompt' && (
                                <input 
                                    type="text" 
                                    className="ws-modal-input" 
                                    autoFocus
                                    placeholder="Ex: WhatsApp Vendas"
                                    value={modalConfig.inputValue}
                                    onChange={(e) => setModalConfig(prev => ({...prev, inputValue: e.target.value}))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') modalConfig.onConfirm(modalConfig.inputValue);
                                    }}
                                />
                            )}
                        </div>
                        <div className="ws-modal-footer">
                            <button className="ws-btn-cancel" onClick={modalConfig.onCancel}>Cancelar</button>
                            <button 
                                className={`ws-btn-confirm ${modalConfig.isDanger ? 'danger' : ''}`}
                                onClick={() => modalConfig.onConfirm(modalConfig.inputValue)}
                            >
                                {modalConfig.type === 'prompt' ? 'Adicionar' : 'Sim, Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="ws-page-header">
                <div>
                    <h1>
                        <Phone size={28} color="#00a884" strokeWidth={2.5} /> Gerenciador de Números
                    </h1>
                    <p>Conecte múltiplos aparelhos de WhatsApp para diferentes setores ou atendentes.</p>
                </div>
                <button className="ws-btn-primary add-btn" onClick={handleAddInstanceClick}>
                    <Plus size={18} strokeWidth={2.5} /> Novo Número
                </button>
            </div>

            {instances.length === 0 ? (
                <div className="ws-empty-state-global">
                    <div className="ws-empty-icon"><QrCode size={56} color="#00a884" opacity={0.5} /></div>
                    <h3>Nenhum WhatsApp Conectado</h3>
                    <p>Adicione seu primeiro número e gere o QR Code para começar a atender seus clientes pela plataforma.</p>
                    <button className="ws-btn-primary" onClick={handleAddInstanceClick}>
                        <Plus size={18} /> Cadastrar Meu Primeiro Número
                    </button>
                </div>
            ) : (
                <div className="ws-grid">
                    {instances.map(inst => (
                        <div key={inst.id} className="ws-card">
                            {/* CABEÇALHO DO CARD */}
                            <div className="ws-card-header">
                                <div className="ws-card-title">
                                    <div className="ws-icon-circle">
                                        <MessageSquare size={18} strokeWidth={2.5} />
                                    </div>
                                    <h2>{inst.name}</h2>
                                </div>
                                <div className={`ws-badge ${inst.status}`}>
                                    <div className="dot"></div>
                                    {inst.status === 'conectado' ? 'Conectado' : inst.status === 'lendo' ? 'Aguardando QR' : 'Desconectado'}
                                </div>
                            </div>

                            {/* CORPO DO CARD */}
                            <div className="ws-card-body">
                                {inst.status === 'conectado' && inst.device ? (
                                    <div className="ws-device-connected">
                                        <div className="ws-avatar-container">
                                            <img src={inst.device.photo || 'https://ui-avatars.com/api/?name=WA&background=25D366&color=fff'} alt="perfil" />
                                            <div className="ws-status-icon"><CheckCircle2 size={16} color="#fff" /></div>
                                        </div>
                                        
                                        <div className="ws-device-details">
                                            <h3>{inst.device.name || 'WhatsApp Principal'}</h3>
                                            <p><Smartphone size={14} /> +{inst.device.number}</p>
                                        </div>

                                        <button className="ws-btn-danger" onClick={() => handleDesconectarClick(inst.id)} disabled={inst.loading}>
                                            {inst.loading ? <RefreshCw className="spin" size={16} /> : <Power size={16} />}
                                            Desconectar Aparelho
                                        </button>
                                    </div>
                                ) : (
                                    <div className="ws-device-disconnected">
                                        {inst.qrCode ? (
                                            <div className="ws-qr-view">
                                                <div className="qr-wrapper">
                                                    <QRCodeCanvas value={inst.qrCode} size={180} level="H" includeMargin={false} />
                                                </div>
                                                <div className="ws-syncing">
                                                    <RefreshCw className="spin" size={14} /> Aguardando leitura...
                                                </div>
                                                <button className="ws-cancel-link" onClick={() => handleDesconectarClick(inst.id)}>
                                                    Cancelar
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="ws-no-qr-view">
                                                <div className="ws-qr-placeholder"><QrCode size={40} /></div>
                                                <p>Aparelho offline. Clique no botão abaixo para gerar um novo QR Code de acesso.</p>
                                                <button className="ws-btn-generate" onClick={() => handleGerarQR(inst.id)} disabled={inst.loading}>
                                                    {inst.loading ? <RefreshCw className="spin" size={16} /> : <QrCode size={16} />}
                                                    Gerar QR Code
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* RODAPÉ DO CARD */}
                            <div className="ws-card-footer">
                                <button className="ws-btn-remove" onClick={() => handleRemoverInstanciaClick(inst.id)}>
                                    <Trash2 size={14} /> Excluir Instância
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <style>{`
                /* Container Principal */
                .ws-container { 
                    padding: 30px; 
                    max-width: 1300px; 
                    margin: 0 auto; 
                    font-family: 'Inter', sans-serif; 
                }

                /* Header da Página */
                .ws-page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 1px solid #e2e8f0;
                }
                .ws-page-header h1 {
                    font-size: 24px;
                    color: #0f172a;
                    margin: 0 0 8px 0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .ws-page-header p {
                    margin: 0;
                    color: #64748b;
                    font-size: 14.5px;
                }

                /* Estado Vazio Global */
                .ws-empty-state-global {
                    background: #fff;
                    border-radius: 20px;
                    padding: 60px 20px;
                    text-align: center;
                    border: 1px dashed #cbd5e1;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02);
                }
                .ws-empty-state-global .ws-empty-icon {
                    margin-bottom: 20px;
                }
                .ws-empty-state-global h3 {
                    color: #1e293b;
                    margin: 0 0 10px 0;
                    font-size: 20px;
                }
                .ws-empty-state-global p {
                    color: #64748b;
                    margin: 0 auto 25px auto;
                    max-width: 400px;
                    line-height: 1.5;
                }

                /* Grid de Cards */
                .ws-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 24px;
                }

                /* Card da Instância */
                .ws-card {
                    background: #fff;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .ws-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04);
                }

                /* Header do Card */
                .ws-card-header {
                    padding: 16px 20px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .ws-card-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .ws-icon-circle {
                    background: #dcfce7;
                    color: #16a34a;
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ws-card-title h2 {
                    margin: 0;
                    font-size: 16px;
                    color: #0f172a;
                    font-weight: 600;
                }

                /* Badges de Status */
                .ws-badge {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .ws-badge.conectado { background: #dcfce7; color: #16a34a; }
                .ws-badge.desconectado { background: #fee2e2; color: #dc2626; }
                .ws-badge.lendo { background: #fef9c3; color: #d97706; }
                .ws-badge .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

                /* Corpo do Card */
                .ws-card-body {
                    padding: 30px 20px;
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Aparelho Conectado */
                .ws-device-connected {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 100%;
                }
                .ws-avatar-container {
                    position: relative;
                    margin-bottom: 16px;
                }
                .ws-avatar-container img {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    border: 3px solid #fff;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
                    object-fit: cover;
                }
                .ws-status-icon {
                    position: absolute;
                    bottom: 0;
                    right: 0;
                    background: #16a34a;
                    border-radius: 50%;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid #fff;
                }
                .ws-device-details {
                    text-align: center;
                    margin-bottom: 24px;
                }
                .ws-device-details h3 {
                    margin: 0 0 6px 0;
                    font-size: 17px;
                    color: #0f172a;
                }
                .ws-device-details p {
                    margin: 0;
                    color: #64748b;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                }

                /* Aparelho Desconectado / QR Code */
                .ws-device-disconnected {
                    width: 100%;
                    text-align: center;
                }
                .ws-no-qr-view p {
                    color: #64748b;
                    font-size: 14px;
                    margin: 15px 0 20px 0;
                    line-height: 1.5;
                }
                .ws-qr-placeholder {
                    color: #cbd5e1;
                }
                .ws-qr-view {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 15px;
                }
                .qr-wrapper {
                    background: #fff;
                    padding: 12px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.02);
                }
                .ws-syncing {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #16a34a;
                    font-size: 13px;
                    font-weight: 500;
                }
                .ws-cancel-link {
                    background: none;
                    border: none;
                    color: #ef4444;
                    font-size: 13px;
                    text-decoration: underline;
                    cursor: pointer;
                    padding: 5px;
                }

                /* Footer do Card */
                .ws-card-footer {
                    padding: 12px 20px;
                    background: #f8fafc;
                    border-top: 1px solid #e2e8f0;
                    text-align: right;
                }
                .ws-btn-remove {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    transition: color 0.2s;
                }
                .ws-btn-remove:hover {
                    color: #ef4444;
                }

                /* Botões Universais */
                .ws-btn-primary {
                    background: #10b981;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .ws-btn-primary:hover:not(:disabled) {
                    background: #059669;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
                }
                .ws-btn-primary:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                .ws-btn-primary.add-btn {
                    padding: 12px 24px;
                }

                .ws-btn-danger {
                    background: #fff;
                    color: #ef4444;
                    border: 1px solid #ef4444;
                    border-radius: 8px;
                    padding: 10px 20px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .ws-btn-danger:hover:not(:disabled) {
                    background: #fef2f2;
                }

                .ws-btn-generate {
                    background: #0f172a;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 24px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .ws-btn-generate:hover:not(:disabled) {
                    background: #1e293b;
                    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
                }

                /* ================= MODAL DE CONFIRMAÇÃO / PROMPT ================= */
                .ws-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    backdrop-filter: blur(4px);
                    z-index: 99999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    animation: fadeIn 0.2s ease;
                }
                .ws-modal {
                    background: #fff;
                    width: 90%;
                    max-width: 420px;
                    border-radius: 16px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                    overflow: hidden;
                    animation: slideUp 0.3s ease;
                }
                .ws-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f0f2f5;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .ws-modal-header h3 {
                    margin: 0;
                    font-size: 18px;
                    color: #0f172a;
                }
                .ws-modal-close-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #64748b;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    border-radius: 50%;
                    transition: 0.2s;
                }
                .ws-modal-close-btn:hover {
                    background: #f1f5f9;
                    color: #ef4444;
                }
                .ws-modal-body {
                    padding: 24px;
                }
                .ws-modal-body p {
                    margin: 0 0 16px 0;
                    color: #475569;
                    font-size: 14.5px;
                    line-height: 1.5;
                }
                .ws-modal-input {
                    width: 100%;
                    padding: 12px 14px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 14px;
                    color: #0f172a;
                    outline: none;
                    box-sizing: border-box;
                    transition: border-color 0.2s;
                }
                .ws-modal-input:focus {
                    border-color: #00a884;
                    box-shadow: 0 0 0 3px rgba(0,168,132,0.1);
                }
                .ws-modal-footer {
                    padding: 16px 24px;
                    background: #f8fafc;
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    border-top: 1px solid #f0f2f5;
                }
                .ws-btn-cancel {
                    background: #fff;
                    border: 1px solid #cbd5e1;
                    color: #475569;
                    padding: 10px 16px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .ws-btn-cancel:hover {
                    background: #f1f5f9;
                }
                .ws-btn-confirm {
                    background: #00a884;
                    border: none;
                    color: #fff;
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .ws-btn-confirm:hover {
                    background: #008f6f;
                }
                .ws-btn-confirm.danger {
                    background: #ef4444;
                }
                .ws-btn-confirm.danger:hover {
                    background: #dc2626;
                }

                /* Utilitários */
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

                /* Responsivo */
                @media (max-width: 768px) {
                    .ws-page-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 15px;
                    }
                    .ws-btn-primary.add-btn {
                        width: 100%;
                    }
                    .ws-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default WhatsAppConfig;